import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiGet } from '@/api/client';
import type { Department } from '@/api/types';
import { Alert } from '@/app/components/ui/Alert';
import { Field, Select } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { useActiveCompany } from '@/features/company/CompanyContext';
import { useMe } from '@/features/me/MeContext';
import { visibleDepartments } from '@/features/me/permissions';
import type { DashboardRange } from '@/features/statistics/api';
import { StatusNote } from '@/features/statistics/StatsPanel';
import { RangeSelect } from '@/features/statistics/RangeSelect';
import { listLocations, type Location } from '@/features/vitality/api';
import { BreakdownCard } from '@/features/participation/BreakdownCard';
import { FunnelCard } from '@/features/participation/FunnelCard';
import { SummaryTiles } from '@/features/participation/SummaryTiles';
import { getParticipation, type ParticipationTarget } from '@/features/participation/api';
import { useAsync } from '@/lib/useAsync';

const ALL_LOCATIONS = 'all';

function withId(
  rows: readonly Department[],
): (Department & { id: string; company_id: string | null })[] {
  return rows.map((d) => ({ ...d, id: d.id ?? '', company_id: d.company_id ?? null }));
}

/**
 * `/participation` — Insights › Participation (§ Navigatiemodel,
 * PLAN-FASE2.md WP-03). Always scoped to the active company, like Vitality
 * (WP-02): no company means no funnel to compute.
 */
export function ParticipationPage() {
  const { company } = useActiveCompany();
  const { t } = useTranslation();

  if (company === null) {
    return (
      <section className="page flex flex-col gap-6">
        <PageHeader title={t('participation.page.title')} />
        <p className="text-ink text-sm">{t('participation.page.noScope')}</p>
      </section>
    );
  }

  return <CompanyParticipation companyId={company.id} />;
}

function CompanyParticipation({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const { permissions } = useMe();

  const departmentsState = useAsync(
    () => apiGet('/companies/{companyId}/departments', { params: { companyId } }),
    [companyId],
  );
  const locationsState = useAsync(() => listLocations(companyId), [companyId]);

  const [departmentId, setDepartmentId] = useState<string>('');
  const [locationId, setLocationId] = useState<string>(ALL_LOCATIONS);
  const [range, setRange] = useState<DashboardRange>('last_30_days');

  // Mirrors the API's own gate two (php/api_participation.php's
  // resolveTarget()): companies.participation.view (company_admin/admin) for
  // a company-wide target, departments.users.stats.view — never inherited
  // from company_admin — for a single department otherwise.
  const canViewCompany =
    permissions.isAdmin || permissions.administeredCompanyIds.includes(companyId);

  const departments = departmentsState.data
    ? visibleDepartments(permissions, companyId, withId(departmentsState.data)).filter((d) =>
        permissions.canViewDepartmentStats({ id: d.id, company_id: d.company_id }),
      )
    : [];
  const locations: Location[] = locationsState.data ?? [];

  const effectiveDepartmentId = departmentId || departments[0]?.id || '';

  const target: ParticipationTarget | null = canViewCompany
    ? { type: 'company', id: companyId }
    : effectiveDepartmentId
      ? { type: 'department', id: effectiveDepartmentId }
      : null;

  const result = useAsync(
    () =>
      target
        ? getParticipation(
            target,
            range,
            canViewCompany && locationId !== ALL_LOCATIONS ? locationId : undefined,
          )
        : Promise.resolve(null),
    [JSON.stringify(target), JSON.stringify(range), locationId],
  );

  const data = result.data;
  const status = data?.status;

  const departmentName = (id: string) => departments.find((d) => d.id === id)?.name ?? id;
  const locationName = (id: string) => locations.find((l) => l.id === id)?.name ?? id;

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('participation.page.title')} lead={t('participation.page.lead')} />

      <div className="flex flex-wrap items-end gap-4">
        <RangeSelect value={range} onChange={setRange} className="mb-0 w-44" />

        {canViewCompany && (
          <Field label={t('participation.filters.location.label')} className="mb-0 w-48">
            <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value={ALL_LOCATIONS}>{t('participation.filters.location.all')}</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {!canViewCompany && (
          <Field label={t('participation.filters.department.label')} className="mb-0 w-48">
            <Select
              value={effectiveDepartmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={departments.length === 0}
            >
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </div>

      {!canViewCompany && departments.length === 0 ? (
        <Alert tone="info">{t('participation.page.noDepartments')}</Alert>
      ) : result.error ? (
        <Alert tone="danger">
          {t('statistics.status.loadFailed', { message: result.error.message })}
        </Alert>
      ) : status === 'insufficient_data' ? (
        <StatusNote status="insufficient_data" />
      ) : (
        <>
          <Alert tone="warning">{t('participation.page.disclaimer')}</Alert>

          <SummaryTiles data={data?.status === 'ok' ? data : undefined} />

          <FunnelCard funnel={data?.status === 'ok' ? data.funnel : undefined} />

          <div className="grid gap-6 lg:grid-cols-3">
            <BreakdownCard
              title={t('participation.breakdown.department')}
              rows={data?.status === 'ok' ? data.breakdown.department : undefined}
              nameOf={(row) => departmentName(row.department_id ?? '')}
              emptyMessage={t('participation.breakdown.empty')}
              minGroupSize={data?.min_group_size ?? 0}
            />
            <BreakdownCard
              title={t('participation.breakdown.location')}
              rows={data?.status === 'ok' ? data.breakdown.location : undefined}
              nameOf={(row) => locationName(row.location_id ?? '')}
              emptyMessage={t('participation.breakdown.empty')}
              minGroupSize={data?.min_group_size ?? 0}
            />
            <BreakdownCard
              title={t('participation.breakdown.pilotGroup')}
              rows={data?.status === 'ok' ? data.breakdown.pilot_group : undefined}
              nameOf={(row) => row.name ?? row.pilot_group_id ?? ''}
              emptyMessage={t('participation.breakdown.pilotGroupEmpty')}
              minGroupSize={data?.min_group_size ?? 0}
            />
          </div>
        </>
      )}
    </section>
  );
}
